import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Paper,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  CircularProgress,
  Alert,
  TextField,
  Button,
  Chip,
  Stack,
  IconButton,
  useMediaQuery,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Fade,
  Grow,
  Skeleton,
  alpha,
  InputAdornment,
  Tooltip,
  Zoom,
  Pagination,
  Grid,
  Card,
  CardContent,
  Divider,
} from "@mui/material";
import {
  Event,
  Schedule,
  Place,
  Search,
  Add,
  ArrowForward,
  Delete,
  Clear,
  Lock,
  Today,
  LocationOn,
  Note,
  People,
  CheckCircle,
  Pending,
  Dashboard,
  TrendingUp,
  AccessTime,
  Room,
  CalendarToday,
  Timer,
  Assessment,
  Star,
} from "@mui/icons-material";
import {
  collection,
  getDocs,
  query,
  orderBy,
  doc,
  deleteDoc,
} from "firebase/firestore";
import { db } from "../firebase";

export default function MeetingList() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));

  // States
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [meetingToDelete, setMeetingToDelete] = useState(null);
  const [confirmationText, setConfirmationText] = useState("");
  const [deleteError, setDeleteError] = useState("");
  const [filter, setFilter] = useState("all");
  const [sortBy, setSortBy] = useState("newest");
  const [page, setPage] = useState(1);
  const itemsPerPage = 5;

  const navigate = useNavigate();

  // Fetch meetings
  useEffect(() => {
    const fetchMeetings = async () => {
      try {
        const meetingsRef = collection(db, "meetings");
        const q = query(meetingsRef, orderBy("createdAt", "desc"));
        const querySnapshot = await getDocs(q);

        const meetingsData = [];
        querySnapshot.forEach((doc) => {
          meetingsData.push({ id: doc.id, ...doc.data() });
        });

        setMeetings(meetingsData);
      } catch (err) {
        setError("Gagal memuat daftar rapat");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    const timer = setTimeout(() => {
      fetchMeetings();
    }, 500);

    return () => clearTimeout(timer);
  }, []);

  // Handlers
  const handleDeleteClick = (meeting) => {
    setMeetingToDelete(meeting);
    setDeleteDialogOpen(true);
    setConfirmationText("");
    setDeleteError("");
  };

  const handleDeleteConfirm = async () => {
    if (confirmationText.trim() !== "Herwin Januardi") {
      setDeleteError("Harap ketik 'Herwin Januardi' untuk konfirmasi");
      return;
    }

    try {
      await deleteDoc(doc(db, "meetings", meetingToDelete.id));
      setMeetings(meetings.filter((m) => m.id !== meetingToDelete.id));
      setDeleteDialogOpen(false);
    } catch (err) {
      setDeleteError("Gagal menghapus rapat");
      console.error(err);
    }
  };

  const handleClearSearch = () => {
    setSearchTerm("");
    setPage(1);
  };

  const handlePageChange = (event, value) => {
    setPage(value);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Helper functions
  const getMeetingStatus = (date) => {
    const today = new Date();
    const meetingDate = new Date(date);
    const diffTime = meetingDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays < 0)
      return { label: "Selesai", color: "default", icon: <CheckCircle /> };
    if (diffDays === 0)
      return { label: "Hari Ini", color: "success", icon: <Today /> };
    if (diffDays <= 3)
      return { label: "Segera", color: "warning", icon: <Pending /> };
    return { label: "Akan Datang", color: "info", icon: <Event /> };
  };

  const formatDate = (dateString) => {
    const options = isMobile
      ? { day: "numeric", month: "short", year: "numeric" }
      : {
          weekday: "long",
          year: "numeric",
          month: "long",
          day: "numeric",
        };
    return new Date(dateString).toLocaleDateString("id-ID", options);
  };

  const formatTime = (time) => time;

  // Filter and sort meetings
  const filteredMeetings = useMemo(() => {
    return meetings
      .filter((meeting) => {
        const searchMatch =
          meeting.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          meeting.location.toLowerCase().includes(searchTerm.toLowerCase());

        if (filter === "upcoming") {
          const status = getMeetingStatus(meeting.date);
          return searchMatch && status.label !== "Selesai";
        }
        if (filter === "past") {
          const status = getMeetingStatus(meeting.date);
          return searchMatch && status.label === "Selesai";
        }
        return searchMatch;
      })
      .sort((a, b) => {
        if (sortBy === "newest") {
          return new Date(b.date) - new Date(a.date);
        }
        if (sortBy === "oldest") {
          return new Date(a.date) - new Date(b.date);
        }
        if (sortBy === "alphabetical") {
          return a.name.localeCompare(b.name);
        }
        return 0;
      });
  }, [meetings, searchTerm, filter, sortBy]);

  // Pagination
  const totalPages = Math.ceil(filteredMeetings.length / itemsPerPage);
  const paginatedMeetings = filteredMeetings.slice(
    (page - 1) * itemsPerPage,
    page * itemsPerPage,
  );

  // Dashboard Statistics
  const totalMeetings = meetings.length;
  const upcomingMeetings = meetings.filter(
    (m) => getMeetingStatus(m.date).label !== "Selesai",
  ).length;
  const todayMeetings = meetings.filter(
    (m) => getMeetingStatus(m.date).label === "Hari Ini",
  ).length;
  const completedMeetings = meetings.filter(
    (m) => getMeetingStatus(m.date).label === "Selesai",
  ).length;

  // Most frequent location
  const locationCount = {};
  meetings.forEach((m) => {
    locationCount[m.location] = (locationCount[m.location] || 0) + 1;
  });
  const mostFrequentLocation = Object.keys(locationCount).reduce(
    (a, b) => (locationCount[a] > locationCount[b] ? a : b),
    "",
  );
  const mostFrequentLocationCount = locationCount[mostFrequentLocation] || 0;

  // Average meeting duration
  const calculateDuration = (start, end) => {
    const [startHour, startMinute] = start.split(":").map(Number);
    const [endHour, endMinute] = end.split(":").map(Number);
    const startTotal = startHour * 60 + startMinute;
    const endTotal = endHour * 60 + endMinute;
    return endTotal - startTotal;
  };

  const totalDuration = meetings.reduce((sum, m) => {
    return sum + calculateDuration(m.startTime, m.endTime);
  }, 0);
  const avgDuration =
    meetings.length > 0 ? Math.round(totalDuration / meetings.length) : 0;
  const avgHours = Math.floor(avgDuration / 60);
  const avgMinutes = avgDuration % 60;

  // Loading Skeleton
  if (loading) {
    return (
      <Box sx={{ maxWidth: 1200, mx: "auto", p: { xs: 1, sm: 2, md: 3 } }}>
        <Grid container spacing={3}>
          {[1, 2, 3, 4].map((i) => (
            <Grid item xs={12} sm={6} md={3} key={i}>
              <Skeleton variant="rounded" height={120} />
            </Grid>
          ))}
        </Grid>
        <Skeleton variant="rounded" height={56} sx={{ mt: 3, mb: 3 }} />
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} variant="rounded" height={100} sx={{ mb: 2 }} />
        ))}
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ maxWidth: 800, mx: "auto", mt: 4, p: 2 }}>
        <Alert
          severity="error"
          sx={{ borderRadius: 2 }}
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => window.location.reload()}
            >
              Coba Lagi
            </Button>
          }
        >
          {error}
        </Alert>
      </Box>
    );
  }

  // Stat Cards Data
  const statCards = [
    {
      title: "TOTAL RAPAT",
      value: totalMeetings,
      //icon: <Event sx={{ fontSize: 28 }} />,
      //subtitle: "Total semua agenda rapat",
      gradient:
        "linear-gradient(135deg, #1a237e 0%, #283593 50%, #3949ab 100%)",
    },
    {
      title: "AKAN DATANG",
      value: upcomingMeetings,
      //icon: <TrendingUp sx={{ fontSize: 28 }} />,
      //subtitle: "Rapat yang akan datang",
      gradient:
        "linear-gradient(135deg, #1b5e20 0%, #2e7d32 50%, #388e3c 100%)",
    },
    {
      title: "HARI INI",
      value: todayMeetings,
      //icon: <Today sx={{ fontSize: 28 }} />,
      //subtitle: "Rapat hari ini",
      gradient:
        "linear-gradient(135deg, #e65100 0%, #f57c00 50%, #fb8c00 100%)",
    },
    {
      title: "SELESAI",
      value: completedMeetings,
      //icon: <CheckCircle sx={{ fontSize: 28 }} />,
      //subtitle: "Rapat yang telah selesai",
      gradient:
        "linear-gradient(135deg, #4a148c 0%, #6a1b9a 50%, #8e24aa 100%)",
    },
  ];

  const additionalCards = [
    {
      title: "LOKASI TERBANYAK",
      value: mostFrequentLocation || "Belum ada data",
      icon: <Room sx={{ fontSize: 28 }} />,
      subtitle: `${mostFrequentLocationCount} rapat diselenggarakan di lokasi ini`,
      gradient:
        "linear-gradient(135deg, #00695c 0%, #00897b 50%, #26a69a 100%)",
    },
    {
      title: "RATA-RATA DURASI RAPAT",
      value:
        meetings.length > 0 ? (
          <>
            {avgHours > 0 && `${avgHours} Jam `}
            {avgMinutes > 0 && `${avgMinutes} Menit`}
            {avgHours === 0 && avgMinutes === 0 && "Belum ada data"}
          </>
        ) : (
          "Belum ada data"
        ),
      icon: <Timer sx={{ fontSize: 28 }} />,
      subtitle: `Dari ${meetings.length} rapat yang telah berlangsung`,
      gradient:
        "linear-gradient(135deg, #b71c1c 0%, #c62828 50%, #d32f2f 100%)",
    },
  ];

  return (
    <Box sx={{ maxWidth: 1200, mx: "auto", p: { xs: 1, sm: 2, md: 3 } }}>
      <Fade in timeout={500}>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, sm: 3, md: 4 },
            borderRadius: 4,
            bgcolor: "background.paper",
            border: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`,
            boxShadow: `0 4px 24px ${alpha(theme.palette.common.black, 0.06)}`,
          }}
        >
          {/* Header */}
          <Stack
            direction={isMobile ? "column" : "row"}
            justifyContent="space-between"
            alignItems={isMobile ? "flex-start" : "center"}
            spacing={isMobile ? 2 : 0}
            mb={4}
          >
            <Box>
              <Typography
                variant={isMobile ? "h5" : "h4"}
                sx={{
                  fontWeight: 800,
                  background: `linear-gradient(135deg, #1b5e20 0%, #2e7d32 40%, #4caf50 100%)`,
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                }}
              >
                Dashboard Rapat
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 0.5 }}
              >
                Kelola dan pantau semua agenda rapat Anda
              </Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => navigate("/meetings")}
              sx={{
                borderRadius: 3,
                px: 4,
                py: 1.2,
                textTransform: "none",
                fontWeight: 600,
                bgcolor: "#2e7d32",
                boxShadow: `0 4px 12px ${alpha("#2e7d32", 0.3)}`,
                width: isMobile ? "100%" : "auto",
                transition: "all 0.3s ease",
                "&:hover": {
                  transform: "translateY(-2px)",
                  bgcolor: "#1b5e20",
                  boxShadow: `0 6px 20px ${alpha("#2e7d32", 0.4)}`,
                },
              }}
            >
              Buat Rapat Baru
            </Button>
          </Stack>

          <Grid container spacing={3} sx={{ mb: 4 }}>
            {/* ===== Baris Pertama (4 Card) ===== */}
            {statCards.slice(0, 4).map((card, index) => (
              <Grid key={index} size={{ xs: 12, sm: 6, md: 3 }}>
                <Card
                  sx={{
                    height: "100%",
                    borderRadius: 4,
                    background: card.gradient,
                    color: "#fff",
                    transition: ".3s",
                    "&:hover": {
                      transform: "translateY(-5px)",
                      boxShadow: 8,
                    },
                  }}
                >
                  <CardContent
                    sx={{
                      p: 3,
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Box
                      sx={{
                        flex: 1,
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        alignItems: "center",
                        textAlign: "center",
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        sx={{
                          opacity: 0.85,
                          fontWeight: 700,
                          textTransform: "uppercase",
                        }}
                      >
                        {card.title}
                      </Typography>

                      <Typography variant="h2" fontWeight={700} sx={{ my: 1 }}>
                        {card.value}
                      </Typography>

                      <Typography variant="body2" sx={{ opacity: 0.9 }}>
                        {card.subtitle}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}

            {/* ===== Baris Kedua (2 Card) ===== */}
            {statCards.slice(4).map((card, index) => (
              <Grid key={index + 4} size={{ xs: 12, md: 6 }}>
                <Card
                  sx={{
                    height: "100%",
                    borderRadius: 4,
                    background: card.gradient,
                    color: "#fff",
                    transition: ".3s",
                    "&:hover": {
                      transform: "translateY(-5px)",
                      boxShadow: 8,
                    },
                  }}
                >
                  <CardContent
                    sx={{
                      p: 3,
                      display: "flex",
                      alignItems: "center",
                      gap: 3,
                    }}
                  >
                    <Avatar
                      sx={{
                        width: 68,
                        height: 68,
                        bgcolor: "rgba(255,255,255,.18)",
                        border: "2px solid rgba(255,255,255,.25)",
                      }}
                    >
                      {card.icon}
                    </Avatar>

                    <Box>
                      <Typography
                        variant="subtitle2"
                        sx={{
                          opacity: 0.85,
                          textTransform: "uppercase",
                        }}
                      >
                        {card.title}
                      </Typography>

                      <Typography variant="h4" fontWeight={700} sx={{ my: 1 }}>
                        {card.value}
                      </Typography>

                      <Typography variant="body1" sx={{ opacity: 0.9 }}>
                        {card.subtitle}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* Additional Stats Cards - Equal Height Grid */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            {additionalCards.map((card, index) => (
              <Grid item xs={12} md={6} key={index}>
                <Card
                  sx={{
                    borderRadius: 3,
                    background: card.gradient,
                    color: "white",
                    transition: "all 0.3s ease",
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      boxShadow: `0 8px 28px ${alpha("#000", 0.3)}`,
                    },
                  }}
                >
                  <CardContent
                    sx={{
                      p: 3,
                      flex: 1,
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={2}
                      sx={{ width: "100%" }}
                    >
                      <Avatar
                        sx={{
                          bgcolor: alpha("#ffffff", 0.2),
                          color: "white",
                          width: 52,
                          height: 52,
                          border: `2px solid ${alpha("#ffffff", 0.3)}`,
                          flexShrink: 0,
                        }}
                      >
                        {card.icon}
                      </Avatar>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                          variant="caption"
                          sx={{
                            opacity: 0.8,
                            fontWeight: 500,
                            display: "block",
                          }}
                        >
                          {card.title}
                        </Typography>
                        <Typography
                          variant="h5"
                          fontWeight={700}
                          sx={{ mt: 0.5, wordBreak: "break-word" }}
                        >
                          {card.value}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ opacity: 0.9, mt: 0.5 }}
                        >
                          {card.subtitle}
                        </Typography>
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* Search and Filter Section */}
          <Paper
            elevation={0}
            sx={{
              p: 2.5,
              mb: 3,
              borderRadius: 3,
              bgcolor: alpha(theme.palette.background.default, 0.6),
              border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
            }}
          >
            <Stack spacing={2}>
              <TextField
                fullWidth
                variant="outlined"
                placeholder="Cari rapat berdasarkan nama atau lokasi..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setPage(1);
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search sx={{ color: "action.active" }} />
                    </InputAdornment>
                  ),
                  endAdornment: searchTerm && (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={handleClearSearch}>
                        <Clear fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                  sx: {
                    borderRadius: 2,
                    bgcolor: "background.paper",
                  },
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "&:hover fieldset": {
                      borderColor: "#2e7d32",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#2e7d32",
                    },
                  },
                }}
              />

              <Stack
                direction={isMobile ? "column" : "row"}
                spacing={1.5}
                justifyContent="space-between"
                alignItems={isMobile ? "stretch" : "center"}
              >
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <Chip
                    label="Semua"
                    onClick={() => setFilter("all")}
                    color={filter === "all" ? "primary" : "default"}
                    variant={filter === "all" ? "filled" : "outlined"}
                    sx={{
                      borderRadius: 2,
                      fontWeight: filter === "all" ? 600 : 400,
                    }}
                  />
                  <Chip
                    label="Akan Datang"
                    onClick={() => setFilter("upcoming")}
                    color={filter === "upcoming" ? "success" : "default"}
                    variant={filter === "upcoming" ? "filled" : "outlined"}
                    sx={{
                      borderRadius: 2,
                      fontWeight: filter === "upcoming" ? 600 : 400,
                    }}
                    icon={<Event />}
                  />
                  <Chip
                    label="Selesai"
                    onClick={() => setFilter("past")}
                    color={filter === "past" ? "default" : "default"}
                    variant={filter === "past" ? "filled" : "outlined"}
                    sx={{
                      borderRadius: 2,
                      fontWeight: filter === "past" ? 600 : 400,
                    }}
                    icon={<CheckCircle />}
                  />
                </Stack>

                <Stack direction="row" spacing={1} flexWrap="wrap">
                  <Chip
                    label="Terbaru"
                    onClick={() => {
                      setSortBy("newest");
                      setPage(1);
                    }}
                    variant={sortBy === "newest" ? "filled" : "outlined"}
                    color={sortBy === "newest" ? "primary" : "default"}
                    size="small"
                    sx={{ borderRadius: 2 }}
                  />
                  <Chip
                    label="Terlama"
                    onClick={() => {
                      setSortBy("oldest");
                      setPage(1);
                    }}
                    variant={sortBy === "oldest" ? "filled" : "outlined"}
                    color={sortBy === "oldest" ? "primary" : "default"}
                    size="small"
                    sx={{ borderRadius: 2 }}
                  />
                  <Chip
                    label="A-Z"
                    onClick={() => {
                      setSortBy("alphabetical");
                      setPage(1);
                    }}
                    variant={sortBy === "alphabetical" ? "filled" : "outlined"}
                    color={sortBy === "alphabetical" ? "primary" : "default"}
                    size="small"
                    sx={{ borderRadius: 2 }}
                  />
                </Stack>
              </Stack>
            </Stack>
          </Paper>

          {/* Meeting List */}
          {filteredMeetings.length === 0 ? (
            <Grow in>
              <Box
                sx={{
                  textAlign: "center",
                  py: 8,
                  px: 2,
                  border: `2px dashed ${alpha(theme.palette.divider, 0.5)}`,
                  borderRadius: 3,
                  bgcolor: alpha(theme.palette.background.default, 0.3),
                }}
              >
                {searchTerm ? (
                  <>
                    <Search
                      sx={{ fontSize: 60, color: "text.secondary", mb: 2 }}
                    />
                    <Typography
                      variant="h6"
                      color="text.secondary"
                      gutterBottom
                    >
                      Tidak ada hasil pencarian
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Coba dengan kata kunci yang berbeda
                    </Typography>
                    <Button
                      variant="outlined"
                      onClick={handleClearSearch}
                      sx={{ mt: 2, borderRadius: 2 }}
                    >
                      Hapus Pencarian
                    </Button>
                  </>
                ) : (
                  <>
                    <Event
                      sx={{ fontSize: 60, color: "text.secondary", mb: 2 }}
                    />
                    <Typography
                      variant="h6"
                      color="text.secondary"
                      gutterBottom
                    >
                      Belum ada agenda rapat
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 3 }}
                    >
                      Mulai buat rapat pertama Anda sekarang
                    </Typography>
                    <Button
                      variant="contained"
                      startIcon={<Add />}
                      onClick={() => navigate("/meetings")}
                      sx={{
                        borderRadius: 3,
                        bgcolor: "#2e7d32",
                        "&:hover": {
                          bgcolor: "#1b5e20",
                        },
                      }}
                    >
                      Buat Rapat Pertama
                    </Button>
                  </>
                )}
              </Box>
            </Grow>
          ) : (
            <>
              {/* Meeting List - MUI v7 Enhanced */}
              <List
                sx={{
                  width: "100%",
                  p: 0,
                  display: "flex",
                  flexDirection: "column",
                  gap: 2.5, // MUI v7: menggunakan gap untuk spacing antar item
                }}
              >
                {paginatedMeetings.map((meeting, index) => {
                  const status = getMeetingStatus(meeting.date);
                  const isPast = status.label === "Selesai";
                  const isToday = status.label === "Hari Ini";
                  const isUpcoming =
                    status.label === "Akan Datang" || status.label === "Segera";

                  return (
                    <Grow in timeout={300 + index * 100} key={meeting.id}>
                      <Paper
                        elevation={0}
                        sx={(theme) => ({
                          borderRadius: 3,
                          overflow: "hidden",
                          border: `1px solid ${alpha(
                            isPast
                              ? theme.palette.grey[400]
                              : isToday
                                ? theme.palette.warning.main
                                : theme.palette.primary.main,
                            0.15,
                          )}`,
                          transition: theme.transitions.create(
                            ["transform", "box-shadow", "border-color"],
                            {
                              duration: theme.transitions.duration.standard,
                              easing: theme.transitions.easing.easeInOut,
                            },
                          ),
                          opacity: isPast ? 0.75 : 1,
                          position: "relative",
                          bgcolor: isPast
                            ? alpha(theme.palette.grey[100], 0.3)
                            : "background.paper",
                          "&:hover": {
                            transform: "translateY(-4px)",
                            boxShadow: `0 8px 32px ${alpha(
                              isPast
                                ? theme.palette.grey[500]
                                : theme.palette.primary.main,
                              0.12,
                            )}`,
                            borderColor: isPast
                              ? theme.palette.grey[500]
                              : theme.palette.primary.main,
                            borderWidth: 1.5,
                          },
                        })}
                      >
                        {/* Status Badge - MUI v7 with slotProps */}
                        <Box
                          sx={{
                            position: "absolute",
                            top: 16,
                            right: 16,
                            zIndex: 2,
                          }}
                        >
                          <Chip
                            label={status.label}
                            size="small"
                            color={status.color}
                            icon={status.icon}
                            sx={(theme) => ({
                              borderRadius: 2,
                              fontWeight: 700,
                              px: 0.5,
                              boxShadow: `0 2px 8px ${alpha(
                                isPast
                                  ? theme.palette.grey[500]
                                  : isToday
                                    ? theme.palette.warning.main
                                    : theme.palette.success.main,
                                0.2,
                              )}`,
                              ...(isMobile && {
                                fontSize: "0.6rem",
                                height: 24,
                                "& .MuiChip-icon": {
                                  fontSize: 14,
                                },
                              }),
                              ...(!isMobile && {
                                height: 28,
                                fontSize: "0.75rem",
                              }),
                            })}
                          />
                        </Box>

                        {/* Time Indicator - MUI v7 with new Stack */}
                        {!isPast && (
                          <Stack
                            direction="row"
                            spacing={0.75}
                            alignItems="center"
                            sx={{
                              position: "absolute",
                              top: 16,
                              left: 16,
                              zIndex: 2,
                              bgcolor: alpha(
                                theme.palette.background.paper,
                                0.85,
                              ),
                              backdropFilter: "blur(4px)",
                              px: 1.5,
                              py: 0.75,
                              borderRadius: 2,
                              border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                            }}
                          >
                            <AccessTime
                              sx={{ fontSize: 14, color: "text.secondary" }}
                            />
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              fontWeight={600}
                            >
                              {formatTime(meeting.startTime)} -{" "}
                              {formatTime(meeting.endTime)}
                            </Typography>
                          </Stack>
                        )}

                        {/* Mobile Action Buttons - MUI v7 with new Stack */}
                        {isMobile && (
                          <Stack
                            direction="row"
                            spacing={0.75}
                            sx={{
                              position: "absolute",
                              bottom: 16,
                              right: 16,
                              zIndex: 2,
                              bgcolor: alpha(
                                theme.palette.background.paper,
                                0.9,
                              ),
                              backdropFilter: "blur(4px)",
                              borderRadius: 3,
                              p: 0.5,
                              boxShadow: `0 2px 12px ${alpha(theme.palette.common.black, 0.08)}`,
                            }}
                          >
                            <Tooltip title="Hapus Rapat" arrow placement="top">
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteClick(meeting);
                                }}
                                sx={(theme) => ({
                                  color: "error.main",
                                  bgcolor: alpha(
                                    theme.palette.error.main,
                                    0.08,
                                  ),
                                  borderRadius: 2,
                                  p: 0.75,
                                  "&:hover": {
                                    bgcolor: alpha(
                                      theme.palette.error.main,
                                      0.2,
                                    ),
                                    transform: "scale(1.1)",
                                  },
                                  transition: theme.transitions.create(
                                    ["background-color", "transform"],
                                    {
                                      duration:
                                        theme.transitions.duration.short,
                                    },
                                  ),
                                })}
                              >
                                <Delete sx={{ fontSize: 18 }} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Lihat Detail" arrow placement="top">
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/report/${meeting.id}`);
                                }}
                                sx={(theme) => ({
                                  color: "#2e7d32",
                                  bgcolor: alpha("#2e7d32", 0.08),
                                  borderRadius: 2,
                                  p: 0.75,
                                  "&:hover": {
                                    bgcolor: alpha("#2e7d32", 0.2),
                                    transform: "scale(1.1)",
                                  },
                                  transition: theme.transitions.create(
                                    ["background-color", "transform"],
                                    {
                                      duration:
                                        theme.transitions.duration.short,
                                    },
                                  ),
                                })}
                              >
                                <ArrowForward sx={{ fontSize: 18 }} />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        )}

                        {/* ListItem - MUI v7 with new spacing */}
                        <ListItem
                          alignItems="flex-start"
                          sx={(theme) => ({
                            cursor: "pointer",
                            p: { xs: 2.5, sm: 3, md: 3.5 },
                            pr: { xs: 12, sm: 14, md: 16 },
                            "&:hover": {
                              bgcolor: alpha(
                                isPast ? theme.palette.grey[500] : "#2e7d32",
                                0.03,
                              ),
                            },
                            transition: theme.transitions.create(
                              "background-color",
                              { duration: theme.transitions.duration.standard },
                            ),
                            gap: { xs: 1.5, sm: 2, md: 2.5 },
                          })}
                          onClick={() => navigate(`/report/${meeting.id}`)}
                          secondaryAction={
                            !isMobile && (
                              <Stack
                                direction="row"
                                spacing={1.5}
                                sx={{
                                  pr: 1,
                                  ml: 2,
                                }}
                              >
                                <Tooltip
                                  title="Hapus Rapat"
                                  arrow
                                  placement="left"
                                >
                                  <IconButton
                                    edge="end"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDeleteClick(meeting);
                                    }}
                                    sx={(theme) => ({
                                      color: "error.main",
                                      borderRadius: 2,
                                      p: 1,
                                      "&:hover": {
                                        bgcolor: alpha(
                                          theme.palette.error.main,
                                          0.12,
                                        ),
                                        transform: "scale(1.1) rotate(-3deg)",
                                      },
                                      transition: theme.transitions.create(
                                        ["background-color", "transform"],
                                        {
                                          duration:
                                            theme.transitions.duration.short,
                                        },
                                      ),
                                    })}
                                  >
                                    <Delete sx={{ fontSize: 22 }} />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip
                                  title="Lihat Detail Rapat"
                                  arrow
                                  placement="left"
                                >
                                  <IconButton
                                    edge="end"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      navigate(`/report/${meeting.id}`);
                                    }}
                                    sx={(theme) => ({
                                      color: "#2e7d32",
                                      borderRadius: 2,
                                      p: 1,
                                      "&:hover": {
                                        bgcolor: alpha("#2e7d32", 0.12),
                                        transform: "scale(1.1) translateX(2px)",
                                      },
                                      transition: theme.transitions.create(
                                        ["background-color", "transform"],
                                        {
                                          duration:
                                            theme.transitions.duration.short,
                                        },
                                      ),
                                    })}
                                  >
                                    <ArrowForward sx={{ fontSize: 22 }} />
                                  </IconButton>
                                </Tooltip>
                              </Stack>
                            )
                          }
                        >
                          {/* Avatar - MUI v7 dengan icon di tengah */}
                          <ListItemAvatar
                            sx={{
                              minWidth: { xs: 60, sm: 72, md: 80 },
                              mr: { xs: 1.5, sm: 2, md: 2.5 },
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <Zoom in timeout={300}>
                              <Avatar
                                sx={(theme) => ({
                                  bgcolor: isPast
                                    ? alpha(theme.palette.grey[500], 0.15)
                                    : isToday
                                      ? alpha(theme.palette.warning.main, 0.15)
                                      : alpha("#2e7d32", 0.12),
                                  color: isPast
                                    ? theme.palette.grey[500]
                                    : isToday
                                      ? theme.palette.warning.main
                                      : "#2e7d32",
                                  width: { xs: 48, sm: 56, md: 60 },
                                  height: { xs: 48, sm: 56, md: 60 },
                                  border: `2.5px solid ${
                                    isPast
                                      ? alpha(theme.palette.grey[500], 0.2)
                                      : isToday
                                        ? alpha(theme.palette.warning.main, 0.3)
                                        : alpha("#2e7d32", 0.25)
                                  }`,
                                  transition: theme.transitions.create(
                                    "transform",
                                    {
                                      duration:
                                        theme.transitions.duration.standard,
                                    },
                                  ),
                                  "&:hover": {
                                    transform: "scale(1.05) rotate(-5deg)",
                                  },
                                  // Memastikan icon di tengah
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  "& .MuiSvgIcon-root": {
                                    fontSize: { xs: 24, sm: 28, md: 32 },
                                  },
                                })}
                              >
                                {React.cloneElement(status.icon, {
                                  sx: {
                                    fontSize: { xs: 24, sm: 28, md: 32 },
                                  },
                                })}
                              </Avatar>
                            </Zoom>
                          </ListItemAvatar>

                          {/* Content - MUI v7 with new spacing */}
                          <ListItemText
                            sx={{
                              margin: 0,
                              "& .MuiListItemText-primary": {
                                marginBottom: 0.5,
                              },
                              "& .MuiListItemText-secondary": {
                                marginTop: 0,
                              },
                            }}
                            primary={
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1.5,
                                  flexWrap: "wrap",
                                }}
                              >
                                <Typography
                                  variant={isMobile ? "subtitle1" : "h6"}
                                  component="div"
                                  sx={{
                                    fontWeight: 700,
                                    color: isPast
                                      ? "text.secondary"
                                      : "text.primary",
                                    letterSpacing: -0.3,
                                    fontSize: isMobile ? "1rem" : "1.1rem",
                                  }}
                                >
                                  {meeting.name}
                                </Typography>

                                {/* Day Badge - MUI v7 */}
                                {!isMobile && (
                                  <Chip
                                    label={meeting.day || "Hari"}
                                    size="small"
                                    sx={(theme) => ({
                                      borderRadius: 1.5,
                                      bgcolor: alpha(
                                        theme.palette.primary.main,
                                        0.08,
                                      ),
                                      color: theme.palette.primary.main,
                                      fontWeight: 600,
                                      fontSize: "0.65rem",
                                      height: 22,
                                    })}
                                  />
                                )}
                              </Box>
                            }
                            secondary={
                              <Stack spacing={1.5} sx={{ mt: 0.5 }}>
                                {/* Date and Time - MUI v7 with new Stack */}
                                <Stack
                                  direction="row"
                                  spacing={1.5}
                                  alignItems="center"
                                  flexWrap="wrap"
                                >
                                  <Stack
                                    direction="row"
                                    spacing={1}
                                    alignItems="center"
                                  >
                                    <CalendarToday
                                      sx={{
                                        color: isPast
                                          ? "text.disabled"
                                          : "text.secondary",
                                        fontSize: isMobile ? 16 : 18,
                                      }}
                                    />
                                    <Typography
                                      variant="body2"
                                      color={
                                        isPast
                                          ? "text.disabled"
                                          : "text.secondary"
                                      }
                                      sx={{
                                        fontSize: isMobile
                                          ? "0.8rem"
                                          : "0.875rem",
                                        fontWeight: 500,
                                      }}
                                    >
                                      {formatDate(meeting.date)}
                                    </Typography>
                                  </Stack>

                                  {!isMobile && (
                                    <>
                                      <Box
                                        sx={{
                                          width: 4,
                                          height: 4,
                                          borderRadius: "50%",
                                          bgcolor: "text.disabled",
                                        }}
                                      />
                                      <Stack
                                        direction="row"
                                        spacing={1}
                                        alignItems="center"
                                      >
                                        <AccessTime
                                          sx={{
                                            color: isPast
                                              ? "text.disabled"
                                              : "text.secondary",
                                            fontSize: 18,
                                          }}
                                        />
                                        <Typography
                                          variant="body2"
                                          color={
                                            isPast
                                              ? "text.disabled"
                                              : "text.secondary"
                                          }
                                          sx={{ fontWeight: 500 }}
                                        >
                                          {formatTime(meeting.startTime)} -{" "}
                                          {formatTime(meeting.endTime)}
                                        </Typography>
                                      </Stack>
                                    </>
                                  )}
                                </Stack>

                                {/* Location - MUI v7 */}
                                <Stack
                                  direction="row"
                                  spacing={1}
                                  alignItems="center"
                                >
                                  <LocationOn
                                    sx={{
                                      color: isPast
                                        ? "text.disabled"
                                        : "text.secondary",
                                      fontSize: isMobile ? 16 : 18,
                                    }}
                                  />
                                  <Typography
                                    variant="body2"
                                    color={
                                      isPast
                                        ? "text.disabled"
                                        : "text.secondary"
                                    }
                                    sx={{
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      display: "-webkit-box",
                                      WebkitLineClamp: 1,
                                      WebkitBoxOrient: "vertical",
                                      fontSize: isMobile
                                        ? "0.8rem"
                                        : "0.875rem",
                                      fontWeight: 450,
                                    }}
                                  >
                                    {meeting.location}
                                  </Typography>
                                </Stack>

                                {/* Tags and Metadata - MUI v7 */}
                                <Stack
                                  direction="row"
                                  spacing={1}
                                  sx={{
                                    mt: 0.5,
                                    flexWrap: "wrap",
                                    gap: 0.75,
                                  }}
                                >
                                  {meeting.participants && (
                                    <Chip
                                      icon={
                                        <People
                                          sx={{ fontSize: isMobile ? 14 : 16 }}
                                        />
                                      }
                                      label={`${meeting.participants} peserta`}
                                      size="small"
                                      sx={(theme) => ({
                                        bgcolor: alpha(
                                          theme.palette.info.main,
                                          0.08,
                                        ),
                                        color: theme.palette.info.dark,
                                        borderRadius: 2,
                                        fontSize: isMobile
                                          ? "0.6rem"
                                          : "0.7rem",
                                        height: isMobile ? 22 : 26,
                                        fontWeight: 500,
                                        "& .MuiChip-icon": {
                                          fontSize: isMobile ? 14 : 16,
                                        },
                                      })}
                                    />
                                  )}

                                  {meeting.notes && (
                                    <Chip
                                      icon={
                                        <Note
                                          sx={{ fontSize: isMobile ? 14 : 16 }}
                                        />
                                      }
                                      label="Ada Catatan"
                                      size="small"
                                      sx={(theme) => ({
                                        bgcolor: alpha(
                                          theme.palette.warning.main,
                                          0.08,
                                        ),
                                        color: theme.palette.warning.dark,
                                        borderRadius: 2,
                                        fontSize: isMobile
                                          ? "0.6rem"
                                          : "0.7rem",
                                        height: isMobile ? 22 : 26,
                                        fontWeight: 500,
                                        "& .MuiChip-icon": {
                                          fontSize: isMobile ? 14 : 16,
                                        },
                                      })}
                                    />
                                  )}

                                  {/* Duration Badge - MUI v7 */}
                                  {!isMobile && (
                                    <Chip
                                      icon={<Timer sx={{ fontSize: 16 }} />}
                                      label={`${calculateDuration(meeting.startTime, meeting.endTime)} menit`}
                                      size="small"
                                      sx={(theme) => ({
                                        bgcolor: alpha(
                                          theme.palette.success.main,
                                          0.08,
                                        ),
                                        color: theme.palette.success.dark,
                                        borderRadius: 2,
                                        fontSize: "0.7rem",
                                        height: 26,
                                        fontWeight: 500,
                                      })}
                                    />
                                  )}

                                  {/* Priority Badge - MUI v7 */}
                                  {meeting.participants &&
                                    meeting.participants > 10 && (
                                      <Chip
                                        icon={<Star sx={{ fontSize: 16 }} />}
                                        label="Prioritas"
                                        size="small"
                                        sx={(theme) => ({
                                          bgcolor: alpha(
                                            theme.palette.error.main,
                                            0.08,
                                          ),
                                          color: theme.palette.error.dark,
                                          borderRadius: 2,
                                          fontSize: "0.7rem",
                                          height: 26,
                                          fontWeight: 600,
                                        })}
                                      />
                                    )}
                                </Stack>
                              </Stack>
                            }
                          />
                        </ListItem>
                      </Paper>
                    </Grow>
                  );
                })}
              </List>
              {/* Pagination */}
              {totalPages > 1 && (
                <Stack
                  direction={isMobile ? "column" : "row"}
                  justifyContent="space-between"
                  alignItems="center"
                  spacing={2}
                  sx={{
                    mt: 3,
                    pt: 2,
                    borderTop: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                  }}
                >
                  <Typography variant="caption" color="text.secondary">
                    Menampilkan {(page - 1) * itemsPerPage + 1} -{" "}
                    {Math.min(page * itemsPerPage, filteredMeetings.length)}{" "}
                    dari {filteredMeetings.length} rapat
                  </Typography>
                  <Pagination
                    count={totalPages}
                    page={page}
                    onChange={handlePageChange}
                    color="primary"
                    size={isMobile ? "small" : "medium"}
                    showFirstButton
                    showLastButton
                    sx={{
                      "& .MuiPaginationItem-root": {
                        borderRadius: 2,
                        "&.Mui-selected": {
                          bgcolor: "#2e7d32",
                          color: "white",
                          "&:hover": {
                            bgcolor: "#1b5e20",
                          },
                        },
                      },
                    }}
                  />
                </Stack>
              )}
            </>
          )}
        </Paper>
      </Fade>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        TransitionComponent={Fade}
        PaperProps={{
          sx: {
            borderRadius: 4,
            p: 1,
          },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Delete sx={{ color: "error.main" }} />
            <Typography variant="h6" fontWeight="bold">
              Konfirmasi Hapus Rapat
            </Typography>
          </Stack>
        </DialogTitle>

        <DialogContent>
          <Alert
            severity="warning"
            sx={{
              mb: 3,
              borderRadius: 2,
              "& .MuiAlert-icon": {
                fontSize: 24,
              },
            }}
          >
            <Typography variant="body2" fontWeight="500">
              Anda akan menghapus rapat:
            </Typography>
            <Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 0.5 }}>
              {meetingToDelete?.name}
            </Typography>
          </Alert>

          <Typography variant="body2" color="text.secondary" gutterBottom>
            Untuk melanjutkan, ketik <strong>"Herwin Januardi"</strong> pada
            form di bawah ini:
          </Typography>

          <TextField
            fullWidth
            value={confirmationText}
            onChange={(e) => setConfirmationText(e.target.value)}
            placeholder='Ketik "Herwin Januardi"'
            sx={{
              mt: 2,
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
                bgcolor: alpha(theme.palette.background.default, 0.5),
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Lock sx={{ color: "action.active" }} />
                </InputAdornment>
              ),
            }}
            autoFocus
          />

          {deleteError && (
            <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>
              {deleteError}
            </Alert>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2, pt: 0 }}>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            sx={{ borderRadius: 2 }}
          >
            Batal
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={confirmationText.trim() !== "Herwin Januardi"}
            sx={{
              borderRadius: 2,
              px: 3,
              boxShadow: `0 4px 12px ${alpha(theme.palette.error.main, 0.3)}`,
              "&:hover": {
                boxShadow: `0 6px 20px ${alpha(theme.palette.error.main, 0.4)}`,
              },
            }}
          >
            Hapus Rapat
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
